import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import { ActionIcon, Grid, Text, TextInput, Tooltip } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { useEffect, useMemo, useState } from 'react';
import { useGetUnpaidOrdersReportData } from '../../../hooks/reports/queries';
import {
  exportUnpaidOrdersReportCSV,
  exportUnpaidOrdersReportPDF
} from '../../../services/reports.service';
import { IconFileSpreadsheet, IconFileTypePdf } from '@tabler/icons-react';
import { MRT_PaginationState } from 'mantine-react-table';
import TableView from './TableView';
import { useDebouncedCallback, useDisclosure } from '@mantine/hooks';
import useDownloadPDF from '../../../hooks/useDownloadPDF';
import MarkAsPaidModal from './MarkAsPaidModal';
import { useGetCashierboxes } from '../../../hooks/cashierboxes/queries';
import { getInitialReportRange, getRangeDate, getReportExclusiveEnd, getReportTimezone } from '../datePickerHelper';
import { buildOrderFolio } from '../../../utils/ordersUtils';
import { IOrder } from '../../../interfaces/Order';

const UnpaidOrdersReport = () => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const location = useLocation();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const downloadPDF = useDownloadPDF();
  const [customerName, setCustomerName] = useState('');
  const [dateRangePicker, setDateRange] = useState<[DateValue, DateValue]>(() => getInitialReportRange(location.search, true));
  const [appliedRange, setAppliedRange] = useState(dateRangePicker);
  const updateDateRange = (value: [DateValue, DateValue]) => {
    setDateRange(value);
    if (value[0] && value[1]) {
      setAppliedRange(value);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    }
  };
  const limitPerReq = 10;
  const debouncedSetCustomer = useDebouncedCallback(setCustomerName, 500);
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: limitPerReq
  });
  const timezone = getReportTimezone(location.search);
  const { fromDate, toDate } = getRangeDate(appliedRange, 'day', timezone, getReportExclusiveEnd(location.search));
  const {
    data,
    isLoading: isGetLoadingOrders,
    isError,
    refetch
  } = useGetUnpaidOrdersReportData(searchParamStoreId, {
    customerName: customerName,
    fromDate,
    toDate,
    timezone: timezone,
    pageNum: pagination.pageIndex,
    limit: limitPerReq
  });
  const [editingOrder, setEditingOrder] = useState(null);
  const [openedMarkAsPaidModal, { open: openMarkAsPaidModal, close: closeMarkAsPaidModal }] =
    useDisclosure(false);
  const { data: cashierboxes } = useGetCashierboxes(!!searchParamStoreId, searchParamStoreId);

  const tableInfo = useMemo(() => {
    return data?.pages?.reduce((list, { tableInfo }: { tableInfo: any[] }) => {
      list.push(...tableInfo);
      return list;
    }, [] as any[]);
  }, [data]);

  const footerInfo = useMemo(() => {
    return data?.pages[0]?.totalRows;
  }, [data?.pages]);

  useEffect(() => {
    if (editingOrder) {
      openMarkAsPaidModal();
    }
  }, [editingOrder]);

  const exportReportPDF = async () => {
    try {
      downloadPDF.download(async () => {
        try {
          return await exportUnpaidOrdersReportPDF(searchParamStoreId, {
            store: searchParamStoreId,
            fromDate,
            toDate,
            timezone: timezone,
            customerName: customerName
          });
        } catch (e) {
          throw e;
        }
      }, `Reporte_Ordenes_Pendientes_De_Pago`);
    } catch (e) {
      console.log(e);
    }
  };

  const totalRowsCount = useMemo(() => data?.pages[0]?.totalRows?.totalRowsCount || -1, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / limitPerReq) || -1, [totalRowsCount]);

  return (
    <CatalogContainer>
      <CatalogListHeader
        title={t('ReportsPage:unpaidOrdersReport.title')}
        showCreateButton={false}
      />
      {!fromDate && !toDate && <Text size="sm" c="dimmed" mb="md">
        {t('ReportsPage:unpaidOrdersReport.allDates')}
      </Text>}
      <Grid>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <TextInput
            label={t('FieldName:customer')}
            placeholder={t('FieldName:customer')}
            onChange={(event) => debouncedSetCustomer(event.currentTarget.value)}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <DatePickerInput
            clearable={false}
            type="range"
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={dateRangePicker}
            onChange={updateDateRange}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label="Descargar listado (PDF)">
            <ActionIcon
              disabled={loadingExport}
              onClick={exportReportPDF}
              variant="filled"
              color="red"
              aria-label="Descargar_Listado_CSV">
              <IconFileTypePdf style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport}
              onClick={() => {
                downloadExcelV2(async () => {
                  try {
                    return await exportUnpaidOrdersReportCSV(searchParamStoreId, {
                      fromDate,
                      toDate,
                      timezone: timezone,
                      customerName: customerName
                    });
                  } catch (e) {
                    throw e;
                  }
                }, `Reporte_${searchParamStoreId}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Listado_CSV">
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>
        <Grid.Col span={12}>
          <MarkAsPaidModal
            order={editingOrder}
            cashierboxes={cashierboxes}
            opened={openedMarkAsPaidModal}
            onCloseModal={() => setEditingOrder(null)}
            onSubmitModal={async () => {
              await refetch();
            }}
            close={closeMarkAsPaidModal}
            title={`${t('ReportsPage:unpaidOrdersReport.addPayment')}: ${buildOrderFolio((editingOrder as unknown as IOrder)?.folioPrefix, (editingOrder as unknown as IOrder)?.sequence)}`}
          />
          <TableView
            tableInfo={tableInfo}
            setEditingOrder={setEditingOrder}
            footerInfo={footerInfo}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isGetLoadingOrders}
            pagination={pagination}
            setPagination={setPagination}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default UnpaidOrdersReport;
